/**
 * @fileoverview Live Stadium Combat Stage and HUD Component for "Farmeando Aura" (/screen).
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (60 FPS, 0% CPU in idle),
 * aggressive neon-skewed fighting game HUD bars, Bangers font numerals, respiratory timer,
 * non-repeating transformation deck cycles (1..50 slots) every 15 taps, comic combo popups
 * (4rem to 9rem with screen shake), turn-based dimming, KO damage flash, and ephemeral confetti.
 * 
 * @module components/Screen/Sections/FarmeandoAura/FarmeandoAuraGame
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetTheme } from '../../../../utilities/BL/FarmeandoAura';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_GAME_CONFIGURATION = {
    gameBackgroundType: 'color',
    gameBackgroundColor: '#0f0f1e',
    gameBackgroundUrl: '',
    gameTextColor: '#ffffff',
    player1Color: '#00ffcc',
    player2Color: '#ff0055',
    fontFamily: 'Bangers',
    player1Avatar: '/img/games/taco/lucho-taquero.png',
    player1Winner: '/img/games/taco/lucho-taquero.png',
    player2Avatar: '/img/games/taco/tunita-taquera.png',
    player2Winner: '/img/games/taco/tunita-taquera.png'
};

/**
 * Standard comic combo tiers specification.
 * @constant {Array<Object>}
 */
const DEFAULT_COMBO_TIERS = [
    { level: 1, threshold: 10, text: '🌟🌟 +10 DE AURA 🌟🌟', extraPoints: 15, fontSize: '4rem', shakeIntensity: 'light' },
    { level: 2, threshold: 20, text: '😎😎 +1,000 DE AURA 😎😎', extraPoints: 20, fontSize: '5rem', shakeIntensity: 'light' },
    { level: 3, threshold: 30, text: '🚀🚀 +10,000 DE AURA 🚀🚀', extraPoints: 25, fontSize: '6rem', shakeIntensity: 'medium' },
    { level: 4, threshold: 40, text: '🔥🔥 +100,000 DE AURA 🔥🔥', extraPoints: 30, fontSize: '7rem', shakeIntensity: 'medium' },
    { level: 5, threshold: 50, text: '⚡⚡ +1,000,000 DE AURA ⚡⚡', extraPoints: 40, fontSize: '8rem', shakeIntensity: 'heavy' },
    { level: 6, threshold: 100, text: '🌌 MODO DIOS 🌌', extraPoints: 50, fontSize: '9rem', shakeIntensity: 'god' }
];

/**
 * Confetti colors palette for victory explosion.
 * @constant {Array<string>}
 */
const CONFETTI_COLOR_PALETTE = ['#00ffcc', '#ff0055', '#ffea00', '#00e5ff', '#ff3366', '#ffffff', '#76ff03'];

/**
 * Generates an array of Fisher-Yates shuffled indices without immediate repetitions on loop.
 * 
 * @function generateShuffledDeckIndices
 * @param {number} deckLength - Number of cards in the deck.
 * @param {number} [lastIndexToAvoid=-1] - Last card index to prevent immediate duplicate on loop.
 * @returns {Array<number>} Shuffled indices.
 */
const generateShuffledDeckIndices = (deckLength, lastIndexToAvoid = -1) => {
    if (deckLength <= 0) return [0];
    if (deckLength === 1) return [0];

    const indicesArray = Array.from({ length: deckLength }, (_, index) => index);

    for (let currentIndex = indicesArray.length - 1; currentIndex > 0; currentIndex--) {
        const randomizedIndex = Math.floor(Math.random() * (currentIndex + 1));
        const temporaryValue = indicesArray[currentIndex];
        indicesArray[currentIndex] = indicesArray[randomizedIndex];
        indicesArray[randomizedIndex] = temporaryValue;
    }

    // Ensure the first element of the new deck is not identical to the last shown item
    if (lastIndexToAvoid >= 0 && indicesArray[0] === lastIndexToAvoid && deckLength > 1) {
        const swapPosition = Math.floor(Math.random() * (deckLength - 1)) + 1;
        const temp = indicesArray[0];
        indicesArray[0] = indicesArray[swapPosition];
        indicesArray[swapPosition] = temp;
    }

    return indicesArray;
};

/**
 * FarmeandoAuraGame Component
 * 
 * @function FarmeandoAuraGame
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - Incoming WebSocket packet or snapshot from parent Screen router.
 * @returns {React.ReactElement} The rendered stadium game HUD and stage.
 */
export const FarmeandoAuraGame = ({ payload }) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA Y CONFIGURACIÓN
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);

    // =========================================================================
    // 2. ESTADO DEL MOTOR DE JUEGO (TELEMETRÍA EN TIEMPO REAL)
    // =========================================================================
    const [combatEngineState, setCombatEngineState] = useState({
        state: 'idle', // idle, game_ready, countdown, playing, paused, finished
        roundDuration: 10,
        gameMode: 'simultaneous', // simultaneous | turns
        startedAt: null,
        endsAt: null,
        remainingSeconds: 10,
        scoreJ1: 0,
        scoreJ2: 0,
        totalTapsJ1: 0,
        totalTapsJ2: 0,
        activeFansJ1: 0,
        activeFansJ2: 0,
        comboJ1: 0,
        comboJ2: 0,
        transformIndexJ1: 0,
        transformIndexJ2: 0,
        winner: null
    });

    // =========================================================================
    // 3. ESTADOS VISUALES DE ANIMACIÓN (ZERO-CANVAS GPU)
    // =========================================================================
    const [countdownDisplayNumber, setCountdownDisplayNumber] = useState(null);
    const [screenShakeClassName, setScreenShakeClassName] = useState('');
    const [activeFloatingCombosList, setActiveFloatingCombosList] = useState([]);
    const [ephemeralConfettiParticleList, setEphemeralConfettiParticleList] = useState([]);
    const [isCinematicDamageFlashActive, setIsCinematicDamageFlashActive] = useState(false);

    // Sprites y Transformaciones de Luchadores J1 y J2
    const [playerOneCurrentSpriteUrl, setPlayerOneCurrentSpriteUrl] = useState('');
    const [playerTwoCurrentSpriteUrl, setPlayerTwoCurrentSpriteUrl] = useState('');
    const [playerOneIsSlidingTransformation, setPlayerOneIsSlidingTransformation] = useState(false);
    const [playerTwoIsSlidingTransformation, setPlayerTwoIsSlidingTransformation] = useState(false);

    // =========================================================================
    // 4. REFERENCIAS MUTABLES PARA GESTIÓN DE MAZOS Y TIMERS
    // =========================================================================
    const playerOneDeckIndicesPoolRef = useRef([]);
    const playerOneDeckPointerRef = useRef(0);
    const playerTwoDeckIndicesPoolRef = useRef([]);
    const playerTwoDeckPointerRef = useRef(0);

    const previousComboLevelJ1Ref = useRef(0);
    const previousComboLevelJ2Ref = useRef(0);
    const previousTransformIndexJ1Ref = useRef(0);
    const previousTransformIndexJ2Ref = useRef(0);

    const localCountdownIntervalTimerRef = useRef(null);
    const screenShakeResetTimerRef = useRef(null);
    const confettiAutoDestroyTimerRef = useRef(null);

    /**
     * Resolves media file paths into fully qualified absolute URLs.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media location.
     * @returns {string} Fully resolved media URL.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
    }, []);

    // =========================================================================
    // 5. CARGA INICIAL DEL TEMA Y ESTADO DE SALA
    // =========================================================================
    const fetchInitialGameConfiguration = useCallback(async () => {
        try {
            const configurationResponse = await GetConfig();
            if (configurationResponse.success && configurationResponse.data) {
                const roomConfig = configurationResponse.data;
                if (roomConfig.activeTheme) {
                    setActiveThemeData(roomConfig.activeTheme);
                } else if (roomConfig.activeThemeId) {
                    const themeResponse = await GetTheme(roomConfig.activeThemeId);
                    if (themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                    }
                }

                setCombatEngineState((previousState) => ({
                    ...previousState,
                    roundDuration: roomConfig.roundDuration || previousState.roundDuration,
                    gameMode: roomConfig.gameMode || previousState.gameMode,
                    state: roomConfig.currentState || previousState.state
                }));
            }
        } catch (initialFetchError) {
            // Non-blocking fallback to default configuration
        }
    }, []);

    useEffect(() => {
        if (!activeThemeData) {
            fetchInitialGameConfiguration();
        }
    }, [activeThemeData, fetchInitialGameConfiguration]);

    // =========================================================================
    // 6. INICIALIZACIÓN DE SPRITES Y BARAJAS DE TRANSFORMACIÓN
    // =========================================================================
    const initializeTransformationDecks = useCallback((themeObject) => {
        const rawDeck = themeObject?.deckImages || [];
        const deckLength = rawDeck.length > 0 ? rawDeck.length : 27;

        playerOneDeckIndicesPoolRef.current = generateShuffledDeckIndices(deckLength);
        playerOneDeckPointerRef.current = 0;

        playerTwoDeckIndicesPoolRef.current = generateShuffledDeckIndices(deckLength);
        playerTwoDeckPointerRef.current = 0;

        // Set initial avatars
        const initialAvatarJ1 = themeObject?.player1Avatar
            ? resolveMediaUrl(themeObject.player1Avatar)
            : (rawDeck[0] ? resolveMediaUrl(rawDeck[0]) : '');

        const initialAvatarJ2 = themeObject?.player2Avatar
            ? resolveMediaUrl(themeObject.player2Avatar)
            : (rawDeck[0] ? resolveMediaUrl(rawDeck[0]) : '');

        setPlayerOneCurrentSpriteUrl(initialAvatarJ1);
        setPlayerTwoCurrentSpriteUrl(initialAvatarJ2);

        previousTransformIndexJ1Ref.current = 0;
        previousTransformIndexJ2Ref.current = 0;
        previousComboLevelJ1Ref.current = 0;
        previousComboLevelJ2Ref.current = 0;
    }, [resolveMediaUrl]);

    useEffect(() => {
        if (activeThemeData) {
            initializeTransformationDecks(activeThemeData);
        }
    }, [activeThemeData, initializeTransformationDecks]);

    // =========================================================================
    // 7. DISPARADOR DE TRANSFORMACIÓN SIN REPETICIÓN (CADA 15 TAPS PROMEDIO)
    // =========================================================================
    const triggerPlayerTransformation = useCallback((playerSide, themeObject) => {
        const rawDeck = themeObject?.deckImages || [];
        const deckCount = rawDeck.length > 0 ? rawDeck.length : 1;

        if (playerSide === 'J1') {
            let nextPointer = playerOneDeckPointerRef.current + 1;
            if (nextPointer >= playerOneDeckIndicesPoolRef.current.length) {
                const lastCardIndex = playerOneDeckIndicesPoolRef.current[playerOneDeckIndicesPoolRef.current.length - 1];
                playerOneDeckIndicesPoolRef.current = generateShuffledDeckIndices(deckCount, lastCardIndex);
                nextPointer = 0;
            }
            playerOneDeckPointerRef.current = nextPointer;

            const nextCardSlotIndex = playerOneDeckIndicesPoolRef.current[nextPointer];
            const nextSpriteUrl = rawDeck[nextCardSlotIndex]
                ? resolveMediaUrl(rawDeck[nextCardSlotIndex])
                : (themeObject?.player1Avatar ? resolveMediaUrl(themeObject.player1Avatar) : '');

            // GPU Accelerated Slide-Out -> Slide-In
            setPlayerOneIsSlidingTransformation(true);
            setTimeout(() => {
                setPlayerOneCurrentSpriteUrl(nextSpriteUrl);
                setTimeout(() => {
                    setPlayerOneIsSlidingTransformation(false);
                }, 180);
            }, 140);
        } else {
            let nextPointer = playerTwoDeckPointerRef.current + 1;
            if (nextPointer >= playerTwoDeckIndicesPoolRef.current.length) {
                const lastCardIndex = playerTwoDeckIndicesPoolRef.current[playerTwoDeckIndicesPoolRef.current.length - 1];
                playerTwoDeckIndicesPoolRef.current = generateShuffledDeckIndices(deckCount, lastCardIndex);
                nextPointer = 0;
            }
            playerTwoDeckPointerRef.current = nextPointer;

            const nextCardSlotIndex = playerTwoDeckIndicesPoolRef.current[nextPointer];
            const nextSpriteUrl = rawDeck[nextCardSlotIndex]
                ? resolveMediaUrl(rawDeck[nextCardSlotIndex])
                : (themeObject?.player2Avatar ? resolveMediaUrl(themeObject.player2Avatar) : '');

            // GPU Accelerated Slide-Out -> Slide-In
            setPlayerTwoIsSlidingTransformation(true);
            setTimeout(() => {
                setPlayerTwoCurrentSpriteUrl(nextSpriteUrl);
                setTimeout(() => {
                    setPlayerTwoIsSlidingTransformation(false);
                }, 180);
            }, 140);
        }
    }, [resolveMediaUrl]);

    // =========================================================================
    // 8. DISPARADOR DE COMBOS POPUP Y SCREEN-SHAKE GPU
    // =========================================================================
    const triggerComicComboPopup = useCallback((playerSide, comboLevel, themeObject) => {
        if (comboLevel <= 0) return;

        // Obtain combo configuration
        const customCombosList = themeObject?.comboTexts || [];
        const matchedCustomCombo = customCombosList.find((item) => item.level === comboLevel);
        const defaultTier = DEFAULT_COMBO_TIERS.find((item) => item.level === comboLevel) || DEFAULT_COMBO_TIERS[DEFAULT_COMBO_TIERS.length - 1];

        const comboText = matchedCustomCombo?.text || defaultTier.text;
        const comboFontSize = matchedCustomCombo?.fontSize || defaultTier.fontSize;
        const shakeIntensity = defaultTier.shakeIntensity || 'medium';

        const uniqueComboIdentifier = `${playerSide}-${comboLevel}-${Date.now()}`;
        const newComboItem = {
            id: uniqueComboIdentifier,
            side: playerSide,
            text: comboText,
            fontSize: comboFontSize,
            level: comboLevel
        };

        setActiveFloatingCombosList((previousCombos) => [...previousCombos, newComboItem]);

        // GPU Screen Shake Application
        if (screenShakeResetTimerRef.current) {
            clearTimeout(screenShakeResetTimerRef.current);
        }
        setScreenShakeClassName(`aura-screen-game--shake-${shakeIntensity}`);
        screenShakeResetTimerRef.current = setTimeout(() => {
            setScreenShakeClassName('');
        }, 550);

        // Auto remove popup after 1.8s
        setTimeout(() => {
            setActiveFloatingCombosList((previousCombos) =>
                previousCombos.filter((item) => item.id !== uniqueComboIdentifier)
            );
        }, 1800);
    }, []);

    // =========================================================================
    // 9. DISPARADOR DE CONFETI EFÍMERO (3 SEGUNDOS EXACTOS - ZERO LEAKS)
    // =========================================================================
    const spawnEphemeralConfettiBurst = useCallback(() => {
        const TOTAL_CONFETTI_PARTICLES_COUNT = 70;
        const generatedParticles = Array.from({ length: TOTAL_CONFETTI_PARTICLES_COUNT }, (_, particleIndex) => {
            const randomizedColor = CONFETTI_COLOR_PALETTE[Math.floor(Math.random() * CONFETTI_COLOR_PALETTE.length)];
            const randomizedHorizontalPosition = Math.random() * 100; // 0% to 100% viewport width
            const randomizedFallDuration = 1.6 + Math.random() * 1.2; // 1.6s to 2.8s
            const randomizedDelay = Math.random() * 0.4; // 0s to 0.4s
            const randomizedSize = 12 + Math.random() * 16; // 12px to 28px
            const isCircular = Math.random() > 0.5;

            return {
                id: `confetti-${particleIndex}-${Date.now()}`,
                color: randomizedColor,
                leftPercentage: randomizedHorizontalPosition,
                durationSeconds: randomizedFallDuration,
                delaySeconds: randomizedDelay,
                sizePixels: randomizedSize,
                borderRadius: isCircular ? '50%' : '2px'
            };
        });

        setEphemeralConfettiParticleList(generatedParticles);

        if (confettiAutoDestroyTimerRef.current) {
            clearTimeout(confettiAutoDestroyTimerRef.current);
        }

        // Strict 3 seconds auto-destruction guarantee (Zero Memory Leaks)
        confettiAutoDestroyTimerRef.current = setTimeout(() => {
            setEphemeralConfettiParticleList([]);
        }, 3000);
    }, []);

    // =========================================================================
    // 10. CINEMÁTICA DE K.O. Y PODIUM
    // =========================================================================
    const triggerKnockoutCinematic = useCallback((winnerResult, themeObject) => {
        setIsCinematicDamageFlashActive(true);
        setTimeout(() => {
            setIsCinematicDamageFlashActive(false);
        }, 650);

        if (winnerResult === 'J1') {
            const winnerAvatarUrl = themeObject?.player1Winner
                ? resolveMediaUrl(themeObject.player1Winner)
                : playerOneCurrentSpriteUrl;
            setPlayerOneCurrentSpriteUrl(winnerAvatarUrl);
            spawnEphemeralConfettiBurst();
        } else if (winnerResult === 'J2') {
            const winnerAvatarUrl = themeObject?.player2Winner
                ? resolveMediaUrl(themeObject.player2Winner)
                : playerTwoCurrentSpriteUrl;
            setPlayerTwoCurrentSpriteUrl(winnerAvatarUrl);
            spawnEphemeralConfettiBurst();
        } else {
            // Empate
            spawnEphemeralConfettiBurst();
        }
    }, [resolveMediaUrl, playerOneCurrentSpriteUrl, playerTwoCurrentSpriteUrl, spawnEphemeralConfettiBurst]);

    // =========================================================================
    // 11. PROCESAMIENTO DE MENSAJES Y TELEMETRÍA WEBSOCKET
    // =========================================================================
    useEffect(() => {
        if (!payload || !payload.type) return;

        const normalizedMessageType = payload.type.toLowerCase();
        const incomingData = payload.data || payload.payload || {};

        switch (normalizedMessageType) {
            case 'aura.prepare':
            case 'aura_show_game': {
                const snapshotState = payload.state || incomingData;
                if (snapshotState.currentTheme) {
                    setActiveThemeData(snapshotState.currentTheme);
                }
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'game_ready',
                    scoreJ1: 0,
                    scoreJ2: 0,
                    totalTapsJ1: 0,
                    totalTapsJ2: 0,
                    comboJ1: 0,
                    comboJ2: 0,
                    transformIndexJ1: 0,
                    transformIndexJ2: 0,
                    winner: null,
                    remainingSeconds: snapshotState.roundDuration || previous.roundDuration || 10
                }));
                setCountdownDisplayNumber(null);
                setEphemeralConfettiParticleList([]);
                break;
            }

            case 'aura_countdown': {
                const countdownValue = payload.count !== undefined ? payload.count : incomingData.count;
                setCountdownDisplayNumber(countdownValue);
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'countdown',
                    winner: null
                }));
                break;
            }

            case 'aura_round_started': {
                setCountdownDisplayNumber(null);
                const startedAtMs = payload.startedAt || incomingData.startedAt;
                const endsAtMs = payload.endsAt || incomingData.endsAt;
                const roundSec = payload.durationSeconds || incomingData.durationSeconds || 10;
                const gameMode = payload.gameMode || incomingData.gameMode || 'simultaneous';

                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'playing',
                    startedAt: startedAtMs,
                    endsAt: endsAtMs,
                    roundDuration: roundSec,
                    gameMode: gameMode,
                    winner: null,
                    scoreJ1: 0,
                    scoreJ2: 0,
                    totalTapsJ1: 0,
                    totalTapsJ2: 0
                }));

                if (activeThemeData) {
                    initializeTransformationDecks(activeThemeData);
                }
                break;
            }

            case 'aura_telemetry': {
                const telemetryData = payload.data || payload;
                const newScoreJ1 = telemetryData.scoreJ1 ?? combatEngineState.scoreJ1;
                const newScoreJ2 = telemetryData.scoreJ2 ?? combatEngineState.scoreJ2;
                const newComboJ1 = telemetryData.comboJ1 ?? 0;
                const newComboJ2 = telemetryData.comboJ2 ?? 0;
                const newTransformJ1 = telemetryData.transformIndexJ1 ?? 0;
                const newTransformJ2 = telemetryData.transformIndexJ2 ?? 0;

                // Check for new combos
                if (newComboJ1 > previousComboLevelJ1Ref.current) {
                    triggerComicComboPopup('J1', newComboJ1, activeThemeData);
                    previousComboLevelJ1Ref.current = newComboJ1;
                }
                if (newComboJ2 > previousComboLevelJ2Ref.current) {
                    triggerComicComboPopup('J2', newComboJ2, activeThemeData);
                    previousComboLevelJ2Ref.current = newComboJ2;
                }

                // Check for new transformations
                if (newTransformJ1 > previousTransformIndexJ1Ref.current) {
                    triggerPlayerTransformation('J1', activeThemeData);
                    previousTransformIndexJ1Ref.current = newTransformJ1;
                }
                if (newTransformJ2 > previousTransformIndexJ2Ref.current) {
                    triggerPlayerTransformation('J2', activeThemeData);
                    previousTransformIndexJ2Ref.current = newTransformJ2;
                }

                setCombatEngineState((previous) => ({
                    ...previous,
                    scoreJ1: newScoreJ1,
                    scoreJ2: newScoreJ2,
                    totalTapsJ1: telemetryData.totalTapsJ1 ?? previous.totalTapsJ1,
                    totalTapsJ2: telemetryData.totalTapsJ2 ?? previous.totalTapsJ2,
                    activeFansJ1: telemetryData.activeFansJ1 ?? previous.activeFansJ1,
                    activeFansJ2: telemetryData.activeFansJ2 ?? previous.activeFansJ2,
                    comboJ1: newComboJ1,
                    comboJ2: newComboJ2,
                    transformIndexJ1: newTransformJ1,
                    transformIndexJ2: newTransformJ2,
                    remainingSeconds: typeof telemetryData.remainingMs === 'number'
                        ? Math.max(0, Math.round((telemetryData.remainingMs / 1000) * 10) / 10)
                        : previous.remainingSeconds
                }));
                break;
            }

            case 'aura_paused': {
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'paused',
                    remainingSeconds: payload.remainingSeconds ?? previous.remainingSeconds
                }));
                break;
            }

            case 'aura_resumed': {
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'playing',
                    endsAt: payload.endsAt ?? previous.endsAt
                }));
                break;
            }

            case 'aura_game_over': {
                const finalWinner = payload.winner || incomingData.winner || 'Empate';
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'finished',
                    winner: finalWinner,
                    remainingSeconds: 0,
                    scoreJ1: payload.scoreJ1 ?? previous.scoreJ1,
                    scoreJ2: payload.scoreJ2 ?? previous.scoreJ2,
                    totalTapsJ1: payload.totalTapsJ1 ?? previous.totalTapsJ1,
                    totalTapsJ2: payload.totalTapsJ2 ?? previous.totalTapsJ2
                }));

                triggerKnockoutCinematic(finalWinner, activeThemeData);
                break;
            }

            case 'aura_reset': {
                setCombatEngineState((previous) => ({
                    ...previous,
                    state: 'idle',
                    scoreJ1: 0,
                    scoreJ2: 0,
                    totalTapsJ1: 0,
                    totalTapsJ2: 0,
                    comboJ1: 0,
                    comboJ2: 0,
                    transformIndexJ1: 0,
                    transformIndexJ2: 0,
                    winner: null,
                    remainingSeconds: previous.roundDuration || 10
                }));
                setCountdownDisplayNumber(null);
                setEphemeralConfettiParticleList([]);
                if (activeThemeData) {
                    initializeTransformationDecks(activeThemeData);
                }
                break;
            }

            default:
                break;
        }
    }, [
        payload,
        activeThemeData,
        combatEngineState.scoreJ1,
        combatEngineState.scoreJ2,
        triggerComicComboPopup,
        triggerPlayerTransformation,
        triggerKnockoutCinematic,
        initializeTransformationDecks
    ]);

    // =========================================================================
    // 12. TEMPORIZADOR LOCAL AUTORITATIVO (0% CPU EN REPOSO)
    // =========================================================================
    useEffect(() => {
        if (combatEngineState.state !== 'playing' || !combatEngineState.endsAt) {
            if (localCountdownIntervalTimerRef.current) {
                clearInterval(localCountdownIntervalTimerRef.current);
                localCountdownIntervalTimerRef.current = null;
            }
            return;
        }

        localCountdownIntervalTimerRef.current = setInterval(() => {
            const currentTimestamp = Date.now();
            const differenceMs = combatEngineState.endsAt - currentTimestamp;
            const updatedSeconds = Math.max(0, Math.round((differenceMs / 1000) * 10) / 10);

            setCombatEngineState((previousState) => {
                if (previousState.state !== 'playing') return previousState;
                return { ...previousState, remainingSeconds: updatedSeconds };
            });

            if (differenceMs <= 0) {
                clearInterval(localCountdownIntervalTimerRef.current);
                localCountdownIntervalTimerRef.current = null;
            }
        }, 100);

        return () => {
            if (localCountdownIntervalTimerRef.current) {
                clearInterval(localCountdownIntervalTimerRef.current);
                localCountdownIntervalTimerRef.current = null;
            }
        };
    }, [combatEngineState.state, combatEngineState.endsAt]);

    // Cleanup effects on unmount
    useEffect(() => {
        return () => {
            if (localCountdownIntervalTimerRef.current) clearInterval(localCountdownIntervalTimerRef.current);
            if (screenShakeResetTimerRef.current) clearTimeout(screenShakeResetTimerRef.current);
            if (confettiAutoDestroyTimerRef.current) clearTimeout(confettiAutoDestroyTimerRef.current);
        };
    }, []);

    // =========================================================================
    // 13. CÁLCULO DE BARRAS DE VIDA Y AURA (ESTILO FIGHTING GAME)
    // =========================================================================
    const gameBackgroundType = activeThemeData?.gameBackgroundType || DEFAULT_GAME_CONFIGURATION.gameBackgroundType;
    const gameBackgroundColor = activeThemeData?.gameBackgroundColor || DEFAULT_GAME_CONFIGURATION.gameBackgroundColor;
    const gameBackgroundUrl = activeThemeData?.gameBackgroundUrl ? resolveMediaUrl(activeThemeData.gameBackgroundUrl) : '';
    const gameTextColor = activeThemeData?.gameTextColor || DEFAULT_GAME_CONFIGURATION.gameTextColor;
    const player1Color = activeThemeData?.player1Color || DEFAULT_GAME_CONFIGURATION.player1Color;
    const player2Color = activeThemeData?.player2Color || DEFAULT_GAME_CONFIGURATION.player2Color;

    // Calcular ancho porcentual de las barras de aura neón
    let playerOneBarPercentage = 100;
    let playerTwoBarPercentage = 100;

    if (combatEngineState.state === 'finished') {
        if (combatEngineState.winner === 'J1') {
            playerOneBarPercentage = 100;
            playerTwoBarPercentage = 0; // K.O.
        } else if (combatEngineState.winner === 'J2') {
            playerOneBarPercentage = 0; // K.O.
            playerTwoBarPercentage = 100;
        } else {
            playerOneBarPercentage = 100;
            playerTwoBarPercentage = 100;
        }
    } else if (combatEngineState.state === 'playing') {
        const totalCombinedScore = combatEngineState.scoreJ1 + combatEngineState.scoreJ2;
        if (totalCombinedScore > 0) {
            playerOneBarPercentage = Math.min(100, Math.max(15, Math.round((combatEngineState.scoreJ1 / totalCombinedScore) * 100)));
            playerTwoBarPercentage = Math.min(100, Math.max(15, Math.round((combatEngineState.scoreJ2 / totalCombinedScore) * 100)));
        } else {
            playerOneBarPercentage = 100;
            playerTwoBarPercentage = 100;
        }
    }

    // Modo Individual (Por Turnos) - Detección de bando en espera
    const isTurnsMode = combatEngineState.gameMode === 'turns';
    const isPlayerOneDimmed = isTurnsMode && combatEngineState.activeFansJ2 > 0 && combatEngineState.activeFansJ1 === 0;
    const isPlayerTwoDimmed = isTurnsMode && combatEngineState.activeFansJ1 > 0 && combatEngineState.activeFansJ2 === 0;

    // =========================================================================
    // 14. RENDERIZADO DEL FONDO DINÁMICO
    // =========================================================================
    const renderCombatBackgroundLayer = () => {
        if (gameBackgroundType === 'video' && gameBackgroundUrl) {
            return (
                <video
                    src={gameBackgroundUrl}
                    className="aura-screen-game__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        if (gameBackgroundType === 'image' && gameBackgroundUrl) {
            return (
                <div
                    className="aura-screen-game__background-image"
                    style={{ backgroundImage: `url(${gameBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="aura-screen-game__background-color"
                style={{ backgroundColor: gameBackgroundColor }}
            />
        );
    };

    // =========================================================================
    // 15. RENDERIZADO PRINCIPAL
    // =========================================================================
    return (
        <div
            className={`aura-screen-game ${screenShakeClassName}`}
            role="region"
            aria-label="Escenario de Combate Farmeando Aura"
        >
            {/* Fondo Dinámico con GPU Compositing */}
            {renderCombatBackgroundLayer()}

            {/* Flash Rojo de Daño Cinemático K.O. */}
            {isCinematicDamageFlashActive && (
                <div className="aura-screen-game__damage-flash-overlay" />
            )}

            {/* Contenedor de Confeti Efímero (Auto-destrucción a los 3s) */}
            {ephemeralConfettiParticleList.length > 0 && (
                <div className="aura-screen-game__confetti-container">
                    {ephemeralConfettiParticleList.map((confettiParticle) => (
                        <div
                            key={confettiParticle.id}
                            className="aura-screen-game__confetti-particle"
                            style={{
                                backgroundColor: confettiParticle.color,
                                left: `${confettiParticle.leftPercentage}%`,
                                width: `${confettiParticle.sizePixels}px`,
                                height: `${confettiParticle.sizePixels * 1.4}px`,
                                borderRadius: confettiParticle.borderRadius,
                                animationDuration: `${confettiParticle.durationSeconds}s`,
                                animationDelay: `${confettiParticle.delaySeconds}s`
                            }}
                        />
                    ))}
                </div>
            )}

            {/* ============================================================= */}
            {/* HUD SUPERIOR: BARRAS NEÓN INCLINADAS Y TEMPORIZADOR CENTRAL    */}
            {/* ============================================================= */}
            <div style={{ zIndex: '1', textAlign: 'center', marginTop: '0.5em' }}>
                <img
                    src="/img/page/adsl-five.png"
                    alt="Logo ADSL"
                    style={{ width: '350px' }}
                />
            </div>

            <header className="aura-screen-game__hud-header">
                {/* LADO IZQUIERDO: BARRAS Y MARCADORES JUGADOR 1 */}
                <div className="aura-screen-game__hud-fighter aura-screen-game__hud-fighter--j1">
                    <div className="aura-screen-game__fighter-meta">
                        <span
                            className="aura-screen-game__fighter-badge"
                            style={{ backgroundColor: player1Color, color: '#000000' }}
                        >
                            JUGADOR 1
                        </span>
                        {/* <span className="aura-screen-game__fans-counter" style={{ color: gameTextColor }}>
                            👥 {combatEngineState.activeFansJ1} FANS
                        </span> */}
                    </div>

                    {/* Barra Neón Inclinada (Fighting Game Style) */}
                    <div className="aura-screen-game__health-bar-wrapper aura-screen-game__health-bar-wrapper--j1">
                        <div
                            className="aura-screen-game__health-bar-fill"
                            style={{
                                width: `${playerOneBarPercentage}%`,
                                background: `linear-gradient(90deg, #ffffff, ${player1Color})`,
                                boxShadow: `0 0 25px ${player1Color}`
                            }}
                        />
                    </div>

                    {/* Marcador Gigante de Aura con Bangers Font */}
                    <div className="aura-screen-game__aura-score-display" style={{ color: gameTextColor }}>
                        <span className="aura-screen-game__score-value" style={{ color: player1Color }}>
                            {Math.round(combatEngineState.scoreJ1)}
                        </span>
                        <span className="aura-screen-game__score-label">PTS AURA</span>
                        <span className="aura-screen-game__taps-sublabel">⚡ {combatEngineState.totalTapsJ1} Taps</span>
                    </div>
                </div>

                {/* CENTRO: TEMPORIZADOR RESPIRATORIO Y ESTADO */}
                <div className="aura-screen-game__hud-center">
                    <div
                        className={`aura-screen-game__timer-badge ${combatEngineState.remainingSeconds <= 3 && combatEngineState.state === 'playing' ? 'aura-screen-game__timer-badge--danger' : ''}`}
                    >
                        <span className="aura-screen-game__timer-digits">
                            {combatEngineState.state === 'finished'
                                ? 'K.O.'
                                : combatEngineState.remainingSeconds.toFixed(1)}
                        </span>
                        <span className="aura-screen-game__timer-label">SEG</span>
                    </div>

                    {combatEngineState.state === 'paused' && (
                        <div className="aura-screen-game__paused-badge">PAUSADO</div>
                    )}
                </div>

                {/* LADO DERECHO: BARRAS Y MARCADORES JUGADOR 2 */}
                <div className="aura-screen-game__hud-fighter aura-screen-game__hud-fighter--j2">
                    <div className="aura-screen-game__fighter-meta aura-screen-game__fighter-meta--j2">
                        {/* <span className="aura-screen-game__fans-counter" style={{ color: gameTextColor }}>
                            👥 {combatEngineState.activeFansJ2} FANS
                        </span> */}
                        <span
                            className="aura-screen-game__fighter-badge"
                            style={{ backgroundColor: player2Color, color: '#000000' }}
                        >
                            JUGADOR 2
                        </span>
                    </div>

                    {/* Barra Neón Inclinada (Fighting Game Style) */}
                    <div className="aura-screen-game__health-bar-wrapper aura-screen-game__health-bar-wrapper--j2">
                        <div
                            className="aura-screen-game__health-bar-fill"
                            style={{
                                width: `${playerTwoBarPercentage}%`,
                                background: `linear-gradient(270deg, #ffffff, ${player2Color})`,
                                boxShadow: `0 0 25px ${player2Color}`
                            }}
                        />
                    </div>

                    {/* Marcador Gigante de Aura con Bangers Font */}
                    <div className="aura-screen-game__aura-score-display aura-screen-game__aura-score-display--j2" style={{ color: gameTextColor }}>
                        <span className="aura-screen-game__score-value" style={{ color: player2Color }}>
                            {Math.round(combatEngineState.scoreJ2)}
                        </span>
                        <span className="aura-screen-game__score-label">PTS AURA</span>
                        <span className="aura-screen-game__taps-sublabel">⚡ {combatEngineState.totalTapsJ2} Taps</span>
                    </div>
                </div>
            </header>

            {/* ============================================================= */}
            {/* ZONA CENTRAL DE COMBATE: LUCHADORES Y ANIMACIONES              */}
            {/* ============================================================= */}
            <main className="aura-screen-game__arena-stage">
                {/* LUCHADOR 1 (LOCAL) */}
                <div
                    className={`aura-screen-game__fighter-spot aura-screen-game__fighter-spot--j1 ${isPlayerOneDimmed ? 'aura-screen-game__fighter-spot--dimmed' : ''}`}
                >
                    <div
                        className="aura-screen-game__aura-energy-halo"
                        style={{ borderColor: player1Color, boxShadow: `0 0 50px ${player1Color}` }}
                    />
                    <img
                        src={playerOneCurrentSpriteUrl || DEFAULT_GAME_CONFIGURATION.player1Avatar}
                        alt="Luchador 1"
                        className={`aura-screen-game__fighter-avatar ${playerOneIsSlidingTransformation ? 'aura-screen-game__fighter-avatar--transforming' : ''} ${combatEngineState.state === 'finished' && combatEngineState.winner === 'J2' ? 'aura-screen-game__fighter-avatar--ko' : ''}`}
                        onError={(imageErrorEvent) => {
                            imageErrorEvent.target.src = DEFAULT_GAME_CONFIGURATION.player1Avatar;
                        }}
                    />
                </div>

                {/* VS CENTRAL O CUENTA REGRESIVA */}
                <div className="aura-screen-game__versus-spot">
                    {countdownDisplayNumber !== null ? (
                        <div className="aura-screen-game__countdown-overlay">
                            <span className="aura-screen-game__countdown-digit">
                                {countdownDisplayNumber > 0 ? countdownDisplayNumber : '¡DALE!'}
                            </span>
                        </div>
                    ) : (
                        <div className="aura-screen-game__vs-badge" style={{ fontSize: '14em' }}>
                            VS
                        </div>
                    )}
                </div>

                {/* LUCHADOR 2 (VISITANTE) */}
                <div
                    className={`aura-screen-game__fighter-spot aura-screen-game__fighter-spot--j2 ${isPlayerTwoDimmed ? 'aura-screen-game__fighter-spot--dimmed' : ''}`}
                >
                    <div
                        className="aura-screen-game__aura-energy-halo"
                        style={{ borderColor: player2Color, boxShadow: `0 0 50px ${player2Color}` }}
                    />
                    <img
                        src={playerTwoCurrentSpriteUrl || DEFAULT_GAME_CONFIGURATION.player2Avatar}
                        alt="Luchador 2"
                        className={`aura-screen-game__fighter-avatar ${playerTwoIsSlidingTransformation ? 'aura-screen-game__fighter-avatar--transforming' : ''} ${combatEngineState.state === 'finished' && combatEngineState.winner === 'J1' ? 'aura-screen-game__fighter-avatar--ko' : ''}`}
                        onError={(imageErrorEvent) => {
                            imageErrorEvent.target.src = DEFAULT_GAME_CONFIGURATION.player2Avatar;
                        }}
                    />
                </div>

                {/* ========================================================= */}
                {/* POPUPS DE COMBO FLOTANTES ESTILO CÓMIC BANGERS            */}
                {/* ========================================================= */}
                {activeFloatingCombosList.map((comboItem) => (
                    <div
                        key={comboItem.id}
                        className={`aura-screen-game__combo-popup aura-screen-game__combo-popup--${comboItem.side.toLowerCase()}`}
                        style={{
                            fontSize: comboItem.fontSize,
                            color: comboItem.side === 'J1' ? player1Color : player2Color
                        }}
                    >
                        {comboItem.text}
                    </div>
                ))}

                {/* ========================================================= */}
                {/* BANNER DE RESULTADO Y PODIUM FINAL                        */}
                {/* ========================================================= */}
                {combatEngineState.state === 'finished' && (
                    <div className="aura-screen-game__podium-banner">
                        <div className="aura-screen-game__podium-banner__card">
                            <img
                                src="/img/page/adsl-five.png"
                                alt="Logo ADSL"
                                style={{ width: '450px' }}
                            />
                            <span className="aura-screen-game__podium-banner__subtitle">
                                ¡FIN DE LA BATALLA!
                            </span>
                            <h2 className="aura-screen-game__podium-banner__title">
                                {combatEngineState.winner === 'J1'
                                    ? '¡GANADOR JUGADOR 1! 🏆'
                                    : combatEngineState.winner === 'J2'
                                        ? '¡GANADOR JUGADOR 2! 🏆'
                                        : '¡EMPATE ÉPICO! ⚡'}
                            </h2>
                            {/* <p className="aura-screen-game__podium-banner__score-summary">
                                J1: {Math.round(combatEngineState.scoreJ1)} pts ({combatEngineState.totalTapsJ1} taps) — VS — J2: {Math.round(combatEngineState.scoreJ2)} pts ({combatEngineState.totalTapsJ2} taps)
                            </p> */}
                        </div>
                    </div>
                )}
            </main>

            <footer className="aura-screen-cover__footer-credits" style={{ fontSize: '26px' }}>
                Hecho con ♥ por Interaktia
            </footer>
        </div>
    );
};

FarmeandoAuraGame.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        count: PropTypes.number,
        startedAt: PropTypes.number,
        endsAt: PropTypes.number,
        durationSeconds: PropTypes.number,
        gameMode: PropTypes.string,
        winner: PropTypes.string,
        scoreJ1: PropTypes.number,
        scoreJ2: PropTypes.number,
        totalTapsJ1: PropTypes.number,
        totalTapsJ2: PropTypes.number,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object
    })
};

FarmeandoAuraGame.defaultProps = {
    payload: null
};
